import React, {useState} from 'react';
import {Pressable, ScrollView, StyleSheet, View} from 'react-native';
import {useTranslation} from 'react-i18next';

import type {Period} from '@/entities/attendance/period';
import type {ControlDepartment} from '@/entities/attendance/types';
import {useBreakpoint} from '@/shared/lib/breakpoints';
import {AppButton} from '@/shared/ui/button';
import {Icon, type IconName} from '@/shared/ui/icons';
import {Sheet} from '@/shared/ui/sheet';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';
import {PeriodPicker} from './PeriodPicker';

/**
 * What the report is scoped to, as a line of pills — and nothing else above the data.
 *
 * The screens used to open with a period card (a row of presets, a month stepper, two
 * date fields, a day count and a help line) stacked on a department card (a second row
 * of chips). Two hundred and fifty points of form before the first number, every visit,
 * for a control most visits leave exactly as they found it. That is a query builder
 * with a report attached, and it is the reason the screen felt like work.
 *
 * Now the scope is a STATEMENT, not a form: «Август 2026», «Все отделы». It is always
 * visible, it is one tap from being changed, and the changing happens in a sheet — off
 * the page, where a form belongs. The page itself starts with the answer.
 *
 * The same bar serves all three rungs of the ladder. A department or a person report
 * has no department filter (it is already scoped), so that pill simply is not passed —
 * which is also why `departments` is optional rather than an empty array standing in
 * for "not applicable".
 */
const styles = StyleSheet.create({
  root: {flexDirection: 'row', alignItems: 'center', gap: spacing(2), flexWrap: 'wrap'},
  /* Телефон: две строки. Первая — чем сужен отчёт (период, отделы), вторая — что с
     ним сделать (табель, выгрузка). В одну строку четыре контрола не помещались и
     переносились «три плюс один», так что «Выгрузка» стояла особняком и читалась
     как что-то третье. */
  phone: {gap: spacing(2)},
  phoneRow: {flexDirection: 'row', alignItems: 'center', gap: spacing(2)},
  phoneAction: {flex: 1},
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(2),
    paddingLeft: spacing(3),
    paddingRight: spacing(2),
    paddingVertical: spacing(2),
    borderRadius: radii.md,
    borderWidth: StyleSheet.hairlineWidth,
    maxWidth: '100%',
  },
  pillLabel: {flexShrink: 1},
  spacer: {flex: 1, minWidth: 0},

  option: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(3),
    paddingHorizontal: spacing(3),
    paddingVertical: spacing(3),
    borderRadius: radii.md,
  },
  optionText: {flex: 1, gap: 1},
  check: {
    width: 20,
    height: 20,
    borderRadius: radii.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sheetBody: {maxHeight: 420},
});

function Pill({
  icon,
  label,
  onPress,
  tone = 'quiet',
  testID,
}: {
  icon: IconName;
  label: string;
  onPress: () => void;
  /** `loud` for the pill that carries the period — the scope everything else hangs off. */
  tone?: 'loud' | 'quiet';
  testID?: string;
}) {
  const p = useTheme();
  const loud = tone === 'loud';
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      testID={testID}
      style={({pressed}) => [
        styles.pill,
        {
          backgroundColor: loud ? p.accent : pressed ? p.muted : p.card,
          borderColor: loud ? p.primary : p.input,
        },
      ]}>
      <Icon name={icon} size={15} color={loud ? p.accentForeground : p.mutedForeground} />
      <AppText
        variant="label"
        numberOfLines={1}
        color={loud ? p.accentForeground : p.foreground}
        style={styles.pillLabel}>
        {label}
      </AppText>
      <Icon
        name="chevronDown"
        size={14}
        color={loud ? p.accentForeground : p.mutedForeground}
      />
    </Pressable>
  );
}

/** One department in the scope sheet — a row with a tick, not a chip in a scroller. */
function Option({
  title,
  subtitle,
  selected,
  onPress,
  testID,
}: {
  title: string;
  subtitle?: string;
  selected: boolean;
  onPress: () => void;
  testID?: string;
}) {
  const p = useTheme();
  return (
    <Pressable
      accessibilityRole="checkbox"
      accessibilityState={{checked: selected}}
      onPress={onPress}
      testID={testID}
      style={({pressed}) => [
        styles.option,
        {backgroundColor: selected ? p.accent : pressed ? p.muted : 'transparent'},
      ]}>
      <View style={styles.optionText}>
        <AppText variant="subtitle" color={selected ? p.accentForeground : p.foreground}>
          {title}
        </AppText>
        {subtitle ? <AppText variant="caption">{subtitle}</AppText> : null}
      </View>
      <View
        style={[
          styles.check,
          {backgroundColor: selected ? p.primary : 'transparent'},
        ]}>
        {selected ? <Icon name="check" size={14} color={p.primaryForeground} /> : null}
      </View>
    </Pressable>
  );
}

export function ReportToolbar({
  period,
  onPeriodChange,
  now,
  periodLabel,
  departments,
  departmentIds,
  onDepartmentsChange,
  /** The export controls, shown in their own sheet behind the ↓ pill. */
  exportSlot,
  /**
   * Табель по выбранным параметрам — СВОЯ плашка и свой лист, а не третья кнопка в
   * листе выгрузки.
   *
   * Он не «ещё один формат того же файла»: это другой файл (матрица вместо трёх
   * плоских листов) и другой жест — не «скачать», а «собрать». Спрятать его под ту же
   * стрелку значило бы предложить выбрать между XLSX, CSV и третьей кнопкой, про
   * которую надо знать заранее, что она открывает форму.
   */
  timesheetSlot,
}: {
  period: Period;
  onPeriodChange: (period: Period) => void;
  now: Date;
  /** Already-formatted, so the pill and the report headline never disagree. */
  periodLabel: string;
  departments?: ControlDepartment[];
  departmentIds?: string[];
  onDepartmentsChange?: (ids: string[]) => void;
  exportSlot?: React.ReactNode;
  timesheetSlot?: React.ReactNode;
}) {
  const {t} = useTranslation();
  const {isDesktop} = useBreakpoint();
  const [open, setOpen] = useState<
    'period' | 'scope' | 'export' | 'timesheet' | null
  >(null);

  const selected = departmentIds ?? [];
  const scopeLabel =
    selected.length === 0
      ? t('reports.allDepartments')
      : selected.length === 1
      ? departments?.find(d => d.department_id === selected[0])?.name ??
        t('reports.filteredBy', {count: 1})
      : t('reports.filteredBy', {count: selected.length});

  const toggleDepartment = (id: string) =>
    onDepartmentsChange?.(
      selected.includes(id)
        ? selected.filter(x => x !== id)
        : [...selected, id],
    );

  const periodPill = (
    <Pill
      icon="attendance"
      tone="loud"
      label={periodLabel || t('reports.period')}
      onPress={() => setOpen('period')}
      testID="toolbar-period"
    />
  );
  const scopePill =
    departments && departments.length > 0 && onDepartmentsChange ? (
      <Pill
        icon="building"
        label={scopeLabel}
        onPress={() => setOpen('scope')}
        testID="toolbar-scope"
      />
    ) : null;

  return (
    <>
      {isDesktop ? (
        <View style={styles.root} testID="report-toolbar">
          {periodPill}
          {scopePill}
          <View style={styles.spacer} />
          {timesheetSlot ? (
            <Pill
              icon="attendance"
              label={t('timesheet.title')}
              onPress={() => setOpen('timesheet')}
              testID="toolbar-timesheet"
            />
          ) : null}
          {exportSlot ? (
            <Pill
              icon="download"
              label={t('reports.export')}
              onPress={() => setOpen('export')}
              testID="toolbar-export"
            />
          ) : null}
        </View>
      ) : (
        <View style={styles.phone} testID="report-toolbar">
          <View style={styles.phoneRow}>
            {periodPill}
            {scopePill}
          </View>
          {timesheetSlot || exportSlot ? (
            <View style={styles.phoneRow}>
              {timesheetSlot ? (
                <AppButton
                  title={t('timesheet.title')}
                  icon="attendance"
                  tone="outline"
                  size="lg"
                  onPress={() => setOpen('timesheet')}
                  style={styles.phoneAction}
                  testID="toolbar-timesheet"
                />
              ) : null}
              {exportSlot ? (
                <AppButton
                  title={t('reports.export')}
                  icon="download"
                  tone="outline"
                  size="lg"
                  onPress={() => setOpen('export')}
                  style={styles.phoneAction}
                  testID="toolbar-export"
                />
              ) : null}
            </View>
          ) : null}
        </View>
      )}

      <Sheet
        visible={open === 'period'}
        onClose={() => setOpen(null)}
        title={t('reports.period')}
        subtitle={t('reports.periodHint')}
        testID="period-sheet">
        <PeriodPicker period={period} onChange={onPeriodChange} now={now} />
      </Sheet>

      <Sheet
        visible={open === 'scope'}
        onClose={() => setOpen(null)}
        title={t('reports.departmentFilter')}
        subtitle={t('reports.departmentFilterHint')}
        testID="scope-sheet">
        <View style={styles.sheetBody}>
          <ScrollView showsVerticalScrollIndicator>
            {/* «Все» is not one option among equals: with nothing selected the server
                reports everyone INCLUDING people attached to no department, whom a
                selection of every department individually silently loses. */}
            <Option
              title={t('reports.allDepartments')}
              subtitle={t('reports.allDepartmentsHint')}
              selected={selected.length === 0}
              onPress={() => onDepartmentsChange?.([])}
              testID="dept-filter-all"
            />
            {(departments ?? []).map(dept => (
              <Option
                key={dept.department_id}
                title={dept.name}
                subtitle={t('reports.headcount', {count: dept.personnel_count})}
                selected={selected.includes(dept.department_id)}
                onPress={() => toggleDepartment(dept.department_id)}
                testID={`dept-filter-${dept.department_id}`}
              />
            ))}
          </ScrollView>
        </View>
      </Sheet>

      <Sheet
        visible={open === 'export'}
        onClose={() => setOpen(null)}
        title={t('reports.export')}
        testID="export-sheet">
        {exportSlot}
      </Sheet>

      <Sheet
        visible={open === 'timesheet'}
        onClose={() => setOpen(null)}
        title={t('timesheet.title')}
        subtitle={t('timesheet.sheetHint')}
        testID="timesheet-sheet">
        {timesheetSlot}
      </Sheet>
    </>
  );
}
